import { Alert, Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import { chargeFieldLabel, formatMoney } from '../../../utils/currency';
import type { ZoneFormState } from '../zoneTypes';
import { activeOnDemandPricing } from '../zoneTypes';
import { ZoneAmountNumberField } from './ZonePricingNumberField';

interface Props {
  form: ZoneFormState;
  setForm: (f: ZoneFormState) => void;
  currency: string;
}

const SAMPLE_BASE = 499;
const SAMPLE_VISIT = 99;

/** Customer pre-tax price for a sample package in this zone — mirrors backend estimatePackagePricing. */
function samplePrice(visitCharge: number, minimumPrice: number) {
  const base = minimumPrice > 0 ? Math.max(SAMPLE_BASE, minimumPrice) : SAMPLE_BASE;
  const visit = visitCharge > 0 ? visitCharge : SAMPLE_VISIT;
  return { base, visit, total: base + visit };
}

export function OnDemandZonePricingSummary({ form, currency }: { form: ZoneFormState; currency: string }) {
  const rows = activeOnDemandPricing(form);
  if (!rows.length) return null;
  return (
    <Stack spacing={0.75}>
      {rows.map((sp) => {
        const visit = Number(sp.rideNow.deliveryCharge ?? 0);
        const minimum = Number(sp.rideNow.minimumFare ?? 0);
        return (
          <Typography key={sp.serviceId} variant="body2">
            <strong>{sp.serviceName}</strong>
            {' — '}visit charge {visit > 0 ? formatMoney(visit, currency) : 'from package'}
            {' · '}minimum package price {minimum > 0 ? formatMoney(minimum, currency) : 'none'}
          </Typography>
        );
      })}
    </Stack>
  );
}

export default function ZoneOnDemandPricingStep({ form, setForm, currency }: Props) {
  const rows = activeOnDemandPricing(form);

  if (!rows.length) {
    return <Typography color="text.secondary">Select a Services (on-demand) service in Step 1.</Typography>;
  }

  const update = (serviceId: string, patch: { deliveryCharge?: number; minimumFare?: number }) => {
    setForm({
      ...form,
      servicePricing: form.servicePricing.map((sp) => (
        sp.serviceId === serviceId ? { ...sp, rideNow: { ...sp.rideNow, ...patch } } : sp
      )),
    });
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>Service pricing for this zone</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Services bookings are priced from the package (base price + visit charge + tax). A zone can only adjust the
        visit charge and set a minimum package price. Distance, per-minute, waiting and ride schedules do not apply.
      </Typography>

      {rows.map((sp, idx) => {
        const visit = Number(sp.rideNow.deliveryCharge ?? 0);
        const minimum = Number(sp.rideNow.minimumFare ?? 0);
        const sample = samplePrice(visit, minimum);
        return (
          <Box key={sp.serviceId} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>{sp.serviceName}</Typography>
            <Typography variant="caption" color="text.secondary">Services · on-demand</Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <ZoneAmountNumberField
                  label={chargeFieldLabel('Zone visit charge', currency)}
                  currency={currency}
                  value={visit}
                  onValueChange={(n) => update(sp.serviceId, { deliveryCharge: n })}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Greater than 0 replaces the package / category visit charge for bookings in this zone. 0 = keep the package visit charge.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <ZoneAmountNumberField
                  label={chargeFieldLabel('Minimum package price', currency)}
                  currency={currency}
                  value={minimum}
                  onValueChange={(n) => update(sp.serviceId, { minimumFare: n })}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Packages with a lower base price are charged this amount in this zone. 0 = no minimum.
                </Typography>
              </Grid>
            </Grid>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Example: package {formatMoney(SAMPLE_BASE, currency)} + visit {formatMoney(SAMPLE_VISIT, currency)}
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Base price{minimum > SAMPLE_BASE ? ' (raised to zone minimum)' : ''}</Typography>
                <Typography variant="body2">{formatMoney(sample.base, currency)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Visit charge{visit > 0 ? ' (zone)' : ' (package)'}</Typography>
                <Typography variant="body2">{formatMoney(sample.visit, currency)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" fontWeight={700}>Customer price before tax</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoney(sample.total, currency)}</Typography>
              </Stack>
            </Paper>
            {idx < rows.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        );
      })}

      <Alert severity="info" sx={{ mt: 1 }}>
        Tax and platform commission come from the package and tax rules. Surge rules are not applied to Services bookings.
      </Alert>
    </Box>
  );
}
